// Prices are stored as strings with a decimal comma and always two decimals: '0,00', '12,50', '-4,95'

/** Matches a stored price: an optional minus, 1 to 5 digits, a comma and two decimals. */
export const PRICE_REGEX = /^-?[0-9]{1,5},[0-9]{2}$/

/** Matches a total of many prices, such as an invoice total: like PRICE_REGEX, without a maximum. */
export const TOTAL_PRICE_REGEX = /^-?[0-9]+,[0-9]{2}$/

/** Parses a price ('12,50', '12.50', '12', 12.5) into a number; empty or unparsable input is 0. */
export function parsePrice(price?: string | number | null): number {
    if (typeof price === 'number') {
        return Number.isFinite(price) ? price : 0
    }

    const value = parseFloat((price || '').replace(',', '.'))

    return Number.isFinite(value) ? value : 0
}

/** Rounds a price up to a multiple of `step` (0.5, 1, 5, ...), in whole cents so a sum like 55.000000000000007 stays 55. */
export function roundUpPrice(price: number, step: number): number {
    const stepCents = Math.round(step * 100)

    return Math.ceil(Math.round(price * 100) / stepCents) * stepCents / 100
}

/**
 * Formats a number, or a price from user input, an API or an import, as a stored price: 12.5 -> '12,50',
 * '5' -> '5,00', '5.5' -> '5,50' and empty -> '0,00'. Never returns '-0,00'. A string that is not a number is
 * returned as it is, so validation rejects it instead of it being stored as 0,00.
 */
export function formatPrice(price?: string | number | null): string {
    let value: number

    if (typeof price === 'number') {
        value = price
    } else {
        const text = (price || '').trim()

        if (!text) {
            return '0,00'
        }

        if (!/^-?[0-9]+([.,][0-9]+)?$/.test(text)) {
            return price
        }

        value = parseFloat(text.replace(',', '.'))
    }

    const fixed = value.toFixed(2)

    return (fixed === '-0.00' ? '0.00' : fixed).replace('.', ',')
}
